import {
  Box,
  Button,
  Grid,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ErrorMessage, Form, Formik, FormikProps } from "formik";
import { useRef, useState, useEffect } from "react";
import * as Yup from "yup";
import {
  addStoreSettings,
  addSiteCredentials,
  useValidateWPCredentialsMutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
interface WordPressSettingProps {
  handleNext: any;
  handleBack: any;
}
interface WordPressFormValues {
  storeName: string;
  storeUrl: string;
  consumerKey: string;
  consumerSecret: string;
}
export const WordPressValidationSchema = Yup.object().shape({
  storeName: Yup.string()
    .required("Store Name is required")
    .max(100, "Store Name must be at most 100 characters"),
  storeUrl: Yup.string()
    .required("Store URL is required")
    .url("Please enter a valid URL"),
  consumerKey: Yup.string()
    .required("Consumer Key is required")
    .min(10, "Consumer Key must be at least 10 characters"),
  consumerSecret: Yup.string()
    .required("Consumer Secret is required")
    .min(10, "Consumer Secret must be at least 10 characters"),
});

export const WordPressSetting: React.FunctionComponent<
  WordPressSettingProps
> = ({ handleNext, handleBack }) => {
  const formRef = useRef<FormikProps<any>>(null);
  const errorMessage =
    "Failed to connect. Please check your credentials and try again.";
  const dmsDealerId = useSelector(
    (state: AppState) => state.authReducer.dmsDealerId,
  );

  const [connectionStatus, setConnectionStatus] = useState({
    isProcessed: false,
    isValid: false,
    errorMessage: "",
  });
  const dispatch = useDispatch();
  const [validateCredentials, validateCredentialsResult] =
    useValidateWPCredentialsMutation();
  const handleConnection = async (values: WordPressFormValues) => {
    validateCredentials({
      StoreUrl: values.storeUrl,
      ConsumerKey: values.consumerKey,
      ConsumerSecret: values.consumerSecret,
      dmsDealerId: dmsDealerId || "",
    }).then((result: any) => {
      if (result.data && result.data.status === 200) {
        setConnectionStatus({
          isProcessed: true,
          isValid: true,
          errorMessage: "",
        });
        dispatch(
          addStoreSettings({
            token: "",
            url: values.storeUrl,
            siteName: values.storeName,
          }),
        );
        let siteCredentials = {
          storeUrl: values.storeUrl,
          consumerKey: values.consumerKey,
          consumerSecret: values.consumerSecret,
          siteDomain: result.data.entity?.storeUrl,
        };
        dispatch(
          addSiteCredentials({ credentials: JSON.stringify(siteCredentials) }),
        );
      } else {
        setConnectionStatus({
          isProcessed: true,
          isValid: false,
          errorMessage: result.data.message || errorMessage,
        });
      }
    });
  };

  return (
    <Box minHeight={"80vh"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          p: 0.5,
          mt: 2,
        }}
      >
        <Button variant="outlined" size="small" onClick={handleBack}>
          Back
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleNext}
          disabled={!(connectionStatus.isProcessed && connectionStatus.isValid)}
        >
          Next
        </Button>
      </Box>

      <Grid container style={{ justifyContent: "center" }}>
        <Grid item xs={12} sm={6}>
          <Formik
            initialValues={{
              storeName: "",
              storeUrl: "",
              consumerKey: "",
              consumerSecret: "",
            }}
            enableReinitialize
            validationSchema={WordPressValidationSchema}
            onSubmit={(values) => {
              handleConnection(values);
            }}
            innerRef={formRef}
          >
            {({ values, handleChange, handleBlur }) => (
              <Form>
                <FormControl
                  sx={{ m: 3 }}
                  component="fieldset"
                  variant="standard"
                  style={{ alignItems: "center" }}
                >
                  <FormLabel component="h2" sx={{ fontSize: "1.5rem", mb: 3 }}>
                    Wordpress Authentication Setting
                  </FormLabel>

                  <Grid container spacing={3} p={2}>
                    {/* Status Alert */}
                    <Grid item sm={12}>
                      {connectionStatus.isProcessed && (
                        <Alert
                          severity={
                            connectionStatus.isValid ? "success" : "error"
                          }
                        >
                          {connectionStatus.isValid
                            ? "Successfully connected to WordPress!"
                            : connectionStatus.errorMessage}
                        </Alert>
                      )}
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="storeName"
                        name="storeName"
                        label="Store Name"
                        fullWidth
                        variant="standard"
                        placeholder="Name your WordPress setup (for reference only)"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.storeName}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="storeName" />}
                        error={
                          !!formRef.current?.errors?.storeName &&
                          !!formRef.current?.touched?.storeName
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="storeUrl"
                        name="storeUrl"
                        label="Store Url"
                        fullWidth
                        variant="standard"
                        placeholder="Store URL"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.storeUrl}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="storeUrl" />}
                        error={
                          !!formRef.current?.errors?.storeUrl &&
                          !!formRef.current?.touched?.storeUrl
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="consumerKey"
                        name="consumerKey"
                        label="Consumer Key"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.consumerKey}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="consumerKey" />}
                        error={
                          !!formRef.current?.errors?.consumerKey &&
                          !!formRef.current?.touched?.consumerKey
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="consumerSecret"
                        name="consumerSecret"
                        label="Consumer Secret"
                        type="password"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.consumerSecret}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="consumerSecret" />}
                        error={
                          !!formRef.current?.errors?.consumerSecret &&
                          !!formRef.current?.touched?.consumerSecret
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <Button
                        variant="contained"
                        type="submit"
                        size="small"
                        disabled={
                          validateCredentialsResult.isLoading ||
                          connectionStatus.isValid
                        }
                        startIcon={
                          validateCredentialsResult.isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {validateCredentialsResult.isLoading
                          ? "Connecting..."
                          : "Connect to Wordpress"}
                      </Button>
                    </Grid>
                  </Grid>
                </FormControl>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WordPressSetting;
